import { useTranslation } from 'react-i18next';

import { AnnouncementActionEvent } from '@bonusly/common-announcements';
import {
	AwardRibbonOutline,
	BookmarkOutline,
	CogOutline,
	HomeOutline,
	MegaphoneOutline,
	PlantOutline,
	ShoppingCartOutline,
	Text,
	UserGroupOutline,
} from '@bonusly/design-system';
import { useDispatch } from '@bonusly/events';
import { useBuildMonarchURL, useBuildRecognizeURL } from '@bonusly/url';
import { Permission, ProtectedContentWithPermissionOrFeatureFlag } from '@bonusly/who-am-i';

import { AppPath, Route } from '../../config/router';
import { useCompanyProfile } from '../../hooks/useCompanyProfile';
import { useIsRecognize } from '../../hooks/useIsRecognize';
import { useUserProfile } from '../../hooks/useUserProfile';
import { Variant } from '../FeatureFlag';
import { MenuItemInformation } from '../Menu/MenuItemInformation';
import { NavDivider } from './NavDivider';
import { NavGroup } from './NavGroup';
import { NavMenuItem } from './NavMenuItem';
import { NavMenuLink } from './NavMenuLink';

export const NavMenuOptionsMobile = ({ onClose }: { onClose: VoidFunction }) => {
	const recognizeURL = useBuildRecognizeURL();
	const monarchURL = useBuildMonarchURL();
	const { user } = useUserProfile();
	const { company } = useCompanyProfile();
	const { t } = useTranslation('navigation');
	const isRecognize = useIsRecognize();
	const isManager = user?.is_manager;
	const dispatch = useDispatch();

	const isLoaded = !!user && !!company;

	if (!isLoaded) return null;

	const handleAnnouncementsClick = () => {
		dispatch(AnnouncementActionEvent.OpenAnnouncement);
		onClose();
	};

	return (
		<>
			<NavGroup data-testid="nav-menu-options-mobile">
				<NavMenuItem colorScheme="blue" icon={<HomeOutline />} to={AppPath.Home}>
					{t('user_menu.home')}
				</NavMenuItem>

				<ProtectedContentWithPermissionOrFeatureFlag
					flag={[Variant.AchieveEarlyAccess, Variant.AchieveLimitedRelease]}
					permissions={Permission.CanAccessAchieve}
				>
					<NavMenuLink colorScheme="blue" icon={<PlantOutline />} onClick={onClose} to={isRecognize ? monarchURL('/growth') : '/growth'}>
						{t('links.growth')}
					</NavMenuLink>
					{isManager && (
						<NavMenuLink colorScheme="blue" icon={<UserGroupOutline />} onClick={onClose} to={isRecognize ? monarchURL('/team') : '/team'}>
							{t('links.team')}
						</NavMenuLink>
					)}
				</ProtectedContentWithPermissionOrFeatureFlag>

				<NavMenuLink colorScheme="blue" icon={<ShoppingCartOutline />} onClick={onClose} to={isRecognize ? monarchURL(AppPath.Rewards) : AppPath.Rewards}>
					<MenuItemInformation
						label={
							<Text as="span" color="gray.700" fontSize="md">
								{t('user_menu.rewards')}
							</Text>
						}
						subLabel={`${user.earning_balance_with_currency} available`}
					/>
				</NavMenuLink>

				{user.show_awards && (
					<NavMenuItem colorScheme="blue" icon={<AwardRibbonOutline />} onClick={onClose} to="/incentives">
						{t('user_menu.incentives')}
					</NavMenuItem>
				)}
			</NavGroup>

			<NavDivider />

			<NavGroup>
				<NavMenuItem icon={<MegaphoneOutline />} onClick={handleAnnouncementsClick}>
					{t('links.announcements')}
				</NavMenuItem>

				<NavMenuItem icon={<BookmarkOutline />} to={AppPath.Bookmark} onClick={onClose}>
					{t('user_menu.bookmarks')}
				</NavMenuItem>

				<NavMenuItem icon={<CogOutline />} to={recognizeURL(Route.Settings)}>
					{t('user_menu.profile_settings')}
				</NavMenuItem>
			</NavGroup>
		</>
	);
};
